import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { geminiSettingsService } from '../../../apiServices/geminiSettingsService';
import { geminiService, ExtractedReceipt, ReceiptImageInput, DEFAULT_GEMINI_MODEL, GEMINI_FREE_MODELS } from '../../../apiServices/geminiService';
import { categoryService, accountService } from '../../../apiServices';
import { useNotification } from '../../../contexts/NotificationContext';
import ReceiptOCRModal from '../../ReceiptOCRModal';
import { Modal, ConfirmationModal, GlassSelect } from '../../UI';
import { clearCachedApiKey } from '../../../apiServices/geminiService';
import './GeminiOCRPage.css';

const GeminiOCRPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [apiKey, setApiKey] = useState('');
  const [originalApiKey, setOriginalApiKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isOCRModalOpen, setIsOCRModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [receiptText, setReceiptText] = useState('');
  const [isProcessingReceipt, setIsProcessingReceipt] = useState(false);
  const [processedReceipts, setProcessedReceipts] = useState<ExtractedReceipt[]>([]);
  const [receiptImageFiles, setReceiptImageFiles] = useState<File[]>([]);
  const [receiptImagePreviews, setReceiptImagePreviews] = useState<string[]>([]);
  const [separateReceiptsMode, setSeparateReceiptsMode] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedModel, setSelectedModel] = useState(() => {
    return localStorage.getItem('gemini-selected-model') || DEFAULT_GEMINI_MODEL;
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    return () => {
      receiptImagePreviews.forEach(url => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });
    };
  }, [receiptImagePreviews]);

  // Обробка paste з буфера обміну
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            processImageFile(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  // Обробка drag-and-drop
  useEffect(() => {
    const dropzone = dropzoneRef.current;
    if (!dropzone) return;

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const validFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
      if (validFiles.length === 0) {
        showNotification('Оберіть файл зображення (PNG, JPG, WEBP...)', 'warning');
      } else {
        validFiles.forEach(f => processImageFile(f));
      }
    };

    dropzone.addEventListener('dragover', handleDragOver);
    dropzone.addEventListener('dragleave', handleDragLeave);
    dropzone.addEventListener('drop', handleDrop);

    return () => {
      dropzone.removeEventListener('dragover', handleDragOver);
      dropzone.removeEventListener('dragleave', handleDragLeave);
      dropzone.removeEventListener('drop', handleDrop);
    };
  }, []);

  const processImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      showNotification('Оберіть файл зображення (PNG, JPG, WEBP...)', 'warning');
      return;
    }
    const maxSizeMb = 10;
    if (file.size > maxSizeMb * 1024 * 1024) {
      showNotification(`Зображення завелике. Максимум ${maxSizeMb}MB`, 'warning');
      return;
    }
    const url = URL.createObjectURL(file);
    setReceiptImageFiles(prev => [...prev, file]);
    setReceiptImagePreviews(prev => [...prev, url]);
  };

  const removeImageFile = (index: number) => {
    const url = receiptImagePreviews[index];
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    setReceiptImageFiles(prev => prev.filter((_, i) => i !== index));
    setReceiptImagePreviews(prev => prev.filter((_, i) => i !== index));
    if (receiptImageFiles.length <= 2) setSeparateReceiptsMode(false);
  };

  const clearSelectedImage = () => {
    receiptImagePreviews.forEach(url => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });
    setReceiptImageFiles([]);
    setReceiptImagePreviews([]);
    setSeparateReceiptsMode(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    Array.from(files).forEach(f => processImageFile(f));
    event.target.value = '';
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = (error) => reject(error);
    });

  const loadCategories = async () => {
    try {
      const data = await categoryService.getAll();
      return data;
    } catch (error) {
      console.error('Помилка завантаження категорій:', error);
      showNotification('Помилка завантаження категорій', 'error');
      return [];
    }
  };

  // Завантажуємо свіжий ключ при відкритті модалки налаштувань
  useEffect(() => {
    if (isSettingsModalOpen) {
      loadApiKey();
    }
  }, [isSettingsModalOpen]);

  const loadApiKey = async (): Promise<string | null> => {
    try {
      const savedKey = await geminiSettingsService.getApiKey();
      if (savedKey) {
        setApiKey(savedKey);
        setOriginalApiKey(savedKey);
        return savedKey;
      } else {
        setApiKey('');
        setOriginalApiKey('');
        return null;
      }
    } catch (error) {
      console.error('Помилка завантаження API ключа:', error);
      return null;
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      showNotification('Введіть API ключ', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      await geminiSettingsService.saveApiKey(apiKey.trim());
      clearCachedApiKey(); // Очищаємо кеш
      setOriginalApiKey(apiKey.trim()); // Оновлюємо оригінальне значення після збереження
      showNotification('API ключ збережено', 'success');
      return true; // Повертаємо true для успішного збереження
    } catch (error) {
      console.error('Помилка збереження API ключа:', error);
      showNotification('Помилка збереження API ключа', 'error');
      return false; // Повертаємо false при помилці
    } finally {
      setIsSaving(false);
    }
  };

  // Перевірка, чи ключ змінився
  const isApiKeyChanged = apiKey.trim() !== originalApiKey;

  const handleProcessReceipt = async () => {
    if (!receiptText.trim() && receiptImageFiles.length === 0) {
      showNotification('Введіть текст чека або додайте фото', 'warning');
      return;
    }

    // Multiple images in "separate receipts" mode → open modal directly (no page-level OCR)
    if (receiptImageFiles.length > 1 && separateReceiptsMode) {
      setIsOCRModalOpen(true);
      return;
    }

    setIsProcessingReceipt(true);
    try {
      // Завантажуємо API ключ перед обробкою
      const loadedApiKey = await loadApiKey();
      
      if (!loadedApiKey) {
        showNotification('Спочатку введіть та збережіть API ключ', 'warning');
        setIsSettingsModalOpen(true);
        return;
      }

      // Завантажуємо категорії перед обробкою
      const loadedCategories = await loadCategories();
      const loadedAccounts = await accountService.getAll();
      
      // Передаємо категорії в сервіс для автоматичного призначення
      const categoriesForGemini = loadedCategories.map(cat => ({
        id: cat.id,
        name: cat.name
      }));
      
      // Convert all selected images to base64
      const imageInputs: ReceiptImageInput[] = await Promise.all(
        receiptImageFiles.map(async (file) => {
          const imageDataUrl = await fileToBase64(file);
          const base64Data = imageDataUrl.split(',')[1];
          if (!base64Data) throw new Error('Не вдалося підготувати фото до обробки');
          return { base64Data, mimeType: file.type || 'image/jpeg' };
        })
      );

      const extractResult = await geminiService.extractItemsFromReceipt(
        receiptText, 
        loadedApiKey,
        categoriesForGemini,
        imageInputs[0],        // backward-compat single image
        selectedModel,
        imageInputs.length > 1 ? imageInputs : undefined,  // multi-image for "один чек"
        loadedAccounts.map(acc => ({ id: acc.id, name: acc.name }))
      );
      
      // Перевіряємо чи був retry
      if (extractResult.retryInfo) {
        const { wasRetry, modelUsed, originalModel, failureReason } = extractResult.retryInfo;
        if (wasRetry) {
          showNotification(
            `Переспроба з моделлю ${modelUsed} (${originalModel} недоступна)`,
            'info'
          );
        } else if (originalModel) {
          showNotification(
            `Успішна переспроба обробки з моделлю ${modelUsed}`,
            'info'
          );
        }
      }
      
      if (extractResult.receipts.length === 0) {
        showNotification('Не вдалося витягти товари з чека', 'warning');
        return;
      }

      setProcessedReceipts(extractResult.receipts);
      setIsOCRModalOpen(true);
      showNotification(
        `Витягнуто ${extractResult.receipts.reduce((sum, r) => sum + r.items.length, 0)} товарів${extractResult.receipts.length > 1 ? ` у ${extractResult.receipts.length} чеках` : ''}`, 
        'success'
      );
    } catch (error: any) {
      console.error('Помилка обробки чека:', error);
      
      const errorMessage = error?.message || '';
      
      // Перевіряємо чи помилка про невалідний API ключ
      if (errorMessage === 'INVALID_API_KEY' || 
          errorMessage.toLowerCase().includes('invalid api key') ||
          errorMessage.toLowerCase().includes('authentication') ||
          errorMessage.toLowerCase().includes('unauthorized')) {
        try {
          // Видаляємо невалідний API ключ
          await geminiSettingsService.deleteApiKey();
          clearCachedApiKey();
          setApiKey('');
          setOriginalApiKey('');
          showNotification('API ключ невалідний і був видалений. Будь ласка, введіть новий API ключ', 'warning');
          setIsSettingsModalOpen(true);
        } catch (deleteError) {
          console.error('Помилка видалення API ключа:', deleteError);
          showNotification('API ключ невалідний. Будь ласка, введіть новий API ключ', 'warning');
          setIsSettingsModalOpen(true);
        }
      }
      // Перевіряємо чи помилка про перевищення квоти (невалідний API ключ)
      else if (errorMessage === 'QUOTA_EXCEEDED' || 
          errorMessage.includes('quota') || 
          errorMessage.includes('exceeded')) {
        try {
          // Видаляємо API ключ
          await geminiSettingsService.deleteApiKey();
          clearCachedApiKey();
          setApiKey('');
          setOriginalApiKey('');
          showNotification('API ключ невалідний або перевищено квоту. Будь ласка, введіть новий API ключ', 'warning');
          setIsSettingsModalOpen(true);
        } catch (deleteError) {
          console.error('Помилка видалення API ключа:', deleteError);
          showNotification('API ключ невалідний або перевищено квоту. Будь ласка, введіть новий API ключ', 'warning');
          setIsSettingsModalOpen(true);
        }
      }
      // Перевіряємо чи помилка про перевантаження моделі
      else if (errorMessage === 'MODEL_OVERLOADED' || 
          errorMessage.includes('overloaded') || 
          errorMessage.includes('try again') ||
          errorMessage.includes('UNAVAILABLE')) {
        showNotification('Модель Gemini втомилася від навантаження. Будь ласка, спробуйте ще раз через кілька хвилин.', 'warning');
      } 
      // Перевіряємо чи помилка про пустий API ключ
      else if (errorMessage.includes('API ключ не налаштовано') || errorMessage.includes('не налаштовано')) {
        showNotification('API ключ не налаштовано. Будь ласка, введіть API ключ', 'warning');
        setIsSettingsModalOpen(true);
      } else {
        showNotification('Помилка обробки чека через GEMINI API', 'error');
      }
    } finally {
      setIsProcessingReceipt(false);
    }
  };

  const handleModelChange = (model: string) => {
    setSelectedModel(model);
    localStorage.setItem('gemini-selected-model', model);
  };

  const hasInputData = Boolean(receiptText.trim() || receiptImageFiles.length > 0);
  const textCharactersCount = receiptText.trim().length;

  return (
    <div className="gemini-ocr-page">
      <div className="gemini-ocr-page-header">
        <div className="gemini-ocr-page-header-content">
          <h2 className="gemini-ocr-page-header-title">Gemini OCR</h2>
          <p className="gemini-ocr-page-header-subtitle">
            Обробка чеків через Google Gemini AI
          </p>
        </div>
        <button
          className="gemini-ocr-settings-btn"
          onClick={() => setIsSettingsModalOpen(true)}
          type="button"
          title="Налаштування API ключа"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        </button>
      </div>

      <div className="gemini-ocr-content">
        <div className="gemini-ocr-card card">
          <div className="gemini-ocr-card-body">
            <div className="gemini-ocr-receipt-input-section">
              <div className="gemini-ocr-input-layout">
                <div className="gemini-ocr-input-panel gemini-ocr-input-panel-image">
                  <div className="gemini-ocr-input-panel-header">
                    <label className="form-label">Фото чека</label>
                    <span className="gemini-ocr-input-panel-badge">OCR</span>
                  </div>

                  {receiptImageFiles.length === 0 ? (
                    <button
                      ref={dropzoneRef}
                      type="button"
                      className={`gemini-ocr-image-dropzone ${isDragOver ? 'drag-over' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isProcessingReceipt}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      <span>Натисніть, щоб додати фото чека</span>
                      <small>Або перетягніть фото, або вставте з буфера (Cmd+V)</small>
                    </button>
                  ) : (
                    <div className="gemini-ocr-multi-images">
                      <div className="gemini-ocr-thumbs-grid">
                        {receiptImageFiles.map((file, idx) => (
                          <div key={idx} className="gemini-ocr-thumb">
                            <img src={receiptImagePreviews[idx]} alt={file.name} />
                            <button
                              type="button"
                              className="gemini-ocr-thumb-remove"
                              onClick={() => removeImageFile(idx)}
                              disabled={isProcessingReceipt}
                              title="Видалити"
                            >×</button>
                            <span className="gemini-ocr-thumb-name">{file.name}</span>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="gemini-ocr-thumb gemini-ocr-thumb-add"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isProcessingReceipt}
                          title="Додати ще фото"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                          </svg>
                          <span>Додати</span>
                        </button>
                      </div>

                      {/* Toggle: one receipt vs separate — only when >1 image */}
                      {receiptImageFiles.length > 1 && (
                        <div className="gemini-ocr-mode-toggle">
                          <button
                            type="button"
                            className={`gemini-ocr-mode-btn${!separateReceiptsMode ? ' active' : ''}`}
                            onClick={() => setSeparateReceiptsMode(false)}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2"/>
                              <path d="M9 9h6M9 12h6M9 15h4"/>
                            </svg>
                            Один чек
                          </button>
                          <button
                            type="button"
                            className={`gemini-ocr-mode-btn${separateReceiptsMode ? ' active' : ''}`}
                            onClick={() => setSeparateReceiptsMode(true)}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="2" y="3" width="9" height="18" rx="1.5"/>
                              <rect x="13" y="3" width="9" height="18" rx="1.5"/>
                            </svg>
                            Окремі чеки
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="gemini-ocr-file-input"
                    onChange={handleImageUpload}
                  />
                </div>

                <div className="gemini-ocr-input-panel gemini-ocr-input-panel-text">
                  <div className="gemini-ocr-input-panel-header">
                    <label className="form-label">Текст чека</label>                    <span className="gemini-ocr-model-badge" title={selectedModel}>
                      {GEMINI_FREE_MODELS.find(m => m.value === selectedModel)?.label || selectedModel}
                    </span>                    <span className="gemini-ocr-input-counter">{textCharactersCount} симв.</span>
                  </div>
                  <textarea
                    className="form-input gemini-ocr-receipt-textarea"
                    value={receiptText}
                    onChange={(e) => setReceiptText(e.target.value)}
                    placeholder="Вставте текст чека, якщо він у вас вже є. Якщо ні, можна обробляти тільки по фото..."
                    rows={10}
                    disabled={isProcessingReceipt}
                  />
                </div>
              </div>

              <div className="gemini-ocr-submit-row">
                <button
                  className="gemini-ocr-process-btn"
                  onClick={handleProcessReceipt}
                  disabled={isProcessingReceipt || !hasInputData}
                >
                  {isProcessingReceipt ? (
                    <>
                      <div className="spinner-small"></div>
                      <span>Обробка...</span>
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      <span>
                        {receiptImageFiles.length > 1 && separateReceiptsMode
                          ? `Відкрити в модалці (${receiptImageFiles.length} фото)`
                          : 'Обробити через GEMINI'}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ReceiptOCRModal
        isOpen={isOCRModalOpen}
        onClose={() => {
          setIsOCRModalOpen(false);
          // Очищаємо дані після закриття модалки
          setReceiptText('');
          clearSelectedImage();
          setProcessedReceipts([]);
        }}
        apiKey={apiKey.trim() || undefined}
        initialReceiptText={!separateReceiptsMode ? receiptText : undefined}
        initialReceipts={processedReceipts}
        initialImages={receiptImageFiles.length > 1 && separateReceiptsMode ? receiptImageFiles : undefined}
        onReceiptCreated={() => {
          setIsOCRModalOpen(false);
          setReceiptText('');
          clearSelectedImage();
          setProcessedReceipts([]);
        }}
      />

      <Modal
        isOpen={isSettingsModalOpen}
        onClose={() => {
          // Якщо є незбережені зміни, показуємо підтвердження
          if (isApiKeyChanged) {
            setShowCloseConfirm(true);
          } else {
            setIsSettingsModalOpen(false);
          }
        }}
        title="Налаштування API ключа"
        size="md"
        closeOnOverlayClick={!isApiKeyChanged}
        footer={(
          <div className="gemini-ocr-api-key-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                // Якщо є незбережені зміни, показуємо підтвердження
                if (isApiKeyChanged) {
                  setShowCloseConfirm(true);
                } else {
                  setIsSettingsModalOpen(false);
                }
              }}
              type="button"
            >
              Скасувати
            </button>
            <button
              className="btn btn-primary"
              onClick={async () => {
                const success = await handleSaveApiKey();
                if (success) {
                  setIsSettingsModalOpen(false);
                }
              }}
              disabled={isSaving || !apiKey.trim() || !isApiKeyChanged}
            >
              {isSaving ? 'Збереження...' : 'Зберегти ключ'}
            </button>
          </div>
        )}
      >
        <div className="gemini-ocr-settings-modal-content">
          <p className="gemini-ocr-description">
            Введіть ваш Google Gemini API ключ. Ключ зберігається безпечно та використовується для обробки чеків.
            Якщо ключ застарів або не працює, ви можете оновити його тут.
          </p>

          <div className="gemini-ocr-api-key-section">
            <label className="form-label">Gemini API Key</label>
            <input
              type="text"
              className="form-input gemini-ocr-api-key-input"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="AIzaSy..."
              disabled={isSaving}
              autoComplete="off"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
            />
          </div>

          <div className="gemini-ocr-model-section">
            <label className="form-label">Модель</label>
            <GlassSelect
              options={GEMINI_FREE_MODELS}
              value={selectedModel}
              onChange={handleModelChange}
              placeholder="Оберіть модель"
            />
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={showCloseConfirm}
        title="Незбережені зміни"
        message="Ви внесли зміни в API ключ. Ви впевнені, що хочете закрити модалку без збереження?"
        confirmText="Так, закрити"
        cancelText="Скасувати"
        onConfirm={() => {
          // Відновлюємо оригінальне значення та закриваємо модалки
          setApiKey(originalApiKey);
          setShowCloseConfirm(false);
          setIsSettingsModalOpen(false);
        }}
        onCancel={() => setShowCloseConfirm(false)}
        variant="warning"
      />
    </div>
  );
};

export default GeminiOCRPage;

